import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  getFromLocalStorage,
  removeFromLocalStorage,
  setToLocalStorage
} from "../lib/utils/storage-utils";

const AUTH_STORAGE_KEY = "smcrc-auth-storage";
const ACCESS_TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";

const AuthContext = createContext(null);

function getInitialState() {
  try {
    const storedAuth = getFromLocalStorage(AUTH_STORAGE_KEY);
    if (!storedAuth) {
      return {
        user: null,
        tokens: null,
        isAuthenticated: false
      };
    }

    const parsed = JSON.parse(storedAuth);
    return {
      user: parsed.user || null,
      tokens: parsed.tokens || null,
      isAuthenticated: Boolean(parsed.tokens?.accessToken)
    };
  } catch (error) {
    console.error("Failed to hydrate auth state", error);
    return {
      user: null,
      tokens: null,
      isAuthenticated: false
    };
  }
}

export function AuthProvider({ children }) {
  const initialState = getInitialState();
  const [user, setUser] = useState(initialState.user);
  const [tokens, setTokens] = useState(initialState.tokens);
  const [isAuthenticated, setIsAuthenticated] = useState(initialState.isAuthenticated);

  useEffect(() => {
    if (tokens?.accessToken) {
      setToLocalStorage(ACCESS_TOKEN_KEY, tokens.accessToken);
    }
    if (tokens?.refreshToken) {
      setToLocalStorage(REFRESH_TOKEN_KEY, tokens.refreshToken);
    }
    if (user || tokens) {
      setToLocalStorage(
        AUTH_STORAGE_KEY,
        JSON.stringify({
          user,
          tokens
        })
      );
    }
  }, [tokens, user]);

  const login = (userData, tokenData) => {
    setUser(userData);
    setTokens(tokenData);
    setIsAuthenticated(true);
  };

  const logout = () => {
    removeFromLocalStorage(AUTH_STORAGE_KEY);
    removeFromLocalStorage(ACCESS_TOKEN_KEY);
    removeFromLocalStorage(REFRESH_TOKEN_KEY);
    setUser(null);
    setTokens(null);
    setIsAuthenticated(false);
  };

  const value = useMemo(
    () => ({
      user,
      tokens,
      isAuthenticated,
      login,
      logout
    }),
    [isAuthenticated, tokens, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthStore() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthStore must be used inside AuthProvider");
  }
  return context;
}
